import {
  Box,
  FileInput,
  Group,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { ExportBrandingConfigData, HeaderAlignment } from '../types';
import SectionHeader from './SectionHeader';

type Props = {
  config: ExportBrandingConfigData;
  onChange: (config: ExportBrandingConfigData) => void;
  onLogoUpload: (logoType: string, file: File) => Promise<void>;
  firstPageLogoKey: string | null;
  subsequentPageLogoKey: string | null;
  footerLogoKey: string | null;
};

const ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Centre' },
  { value: 'right', label: 'Right' },
];

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  const theme = useMantineTheme();
  return (
    <Box
      p="md"
      style={{
        borderRadius: 16,
        border: `1px solid ${theme.colors.gray[2]}`,
      }}
    >
      <Stack gap="sm">
        <Text size="textMd" fw={FONT_WEIGHTS.semibold}>
          {title}
        </Text>
        {children}
      </Stack>
    </Box>
  );
}

export default function PageLayoutSection({
  config,
  onChange,
  onLogoUpload,
  firstPageLogoKey,
  subsequentPageLogoKey,
  footerLogoKey,
}: Props) {
  const handleLogoUpload = async (logoType: string, file: File | null) => {
    if (!file) return;
    try {
      await onLogoUpload(logoType, file);
      notifications.show({
        title: 'Logo uploaded',
        message: `${logoType.replace(/_/g, ' ')} logo uploaded successfully.`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Upload failed',
        message: 'Failed to upload logo.',
        color: 'red',
      });
    }
  };

  return (
    <div>
      <SectionHeader
        title="Page Layout"
        subtitle="Configure headers and footers for exported documents."
      />
      <Stack gap="md">
        <Switch
          label="Different first page header"
          checked={config.different_first_page_header}
          onChange={e =>
            onChange({ ...config, different_first_page_header: e.currentTarget.checked })
          }
        />

        {config.different_first_page_header && (
          <SubSection title="First Page Header">
            <FileInput
              label="Logo image"
              placeholder="Select image file"
              accept="image/*"
              onChange={file => handleLogoUpload('first_page', file)}
              description={
                firstPageLogoKey
                  ? `Current: ${firstPageLogoKey.split('/').pop()}`
                  : 'No logo uploaded'
              }
            />
            <Group>
              <Text size="sm">Alignment:</Text>
              <SegmentedControl
                data={ALIGNMENT_OPTIONS}
                value={config.first_page_header.alignment}
                onChange={v =>
                  onChange({
                    ...config,
                    first_page_header: {
                      ...config.first_page_header,
                      alignment: v as HeaderAlignment,
                    },
                  })
                }
              />
            </Group>
          </SubSection>
        )}

        <SubSection
          title={config.different_first_page_header ? 'Subsequent Pages Header' : 'Page Header'}
        >
          <FileInput
            label="Logo image"
            placeholder="Select image file"
            accept="image/*"
            onChange={file => handleLogoUpload('subsequent_page', file)}
            description={
              subsequentPageLogoKey
                ? `Current: ${subsequentPageLogoKey.split('/').pop()}`
                : 'No logo uploaded'
            }
          />
          <Group>
            <Text size="sm">Alignment:</Text>
            <SegmentedControl
              data={ALIGNMENT_OPTIONS}
              value={config.subsequent_page_header.alignment}
              onChange={v =>
                onChange({
                  ...config,
                  subsequent_page_header: {
                    ...config.subsequent_page_header,
                    alignment: v as HeaderAlignment,
                  },
                })
              }
            />
          </Group>
        </SubSection>

        <SubSection title="Footer">
          <FileInput
            label="Footer logo"
            placeholder="Select image file"
            accept="image/*"
            onChange={file => handleLogoUpload('footer', file)}
            description={
              footerLogoKey ? `Current: ${footerLogoKey.split('/').pop()}` : 'No logo uploaded'
            }
          />
          <Group>
            <Text size="sm">Logo alignment:</Text>
            <SegmentedControl
              data={ALIGNMENT_OPTIONS}
              value={config.footer.logo_alignment}
              onChange={v =>
                onChange({
                  ...config,
                  footer: { ...config.footer, logo_alignment: v as HeaderAlignment },
                })
              }
            />
          </Group>
          <Switch
            label="Show page numbers"
            description="Displays 'Page X of Y' on the opposite side from the logo"
            checked={config.footer.show_page_numbers}
            onChange={e =>
              onChange({
                ...config,
                footer: { ...config.footer, show_page_numbers: e.currentTarget.checked },
              })
            }
          />
        </SubSection>
      </Stack>
    </div>
  );
}
